import type React from 'react';

import { useGetGroup } from '@vlabs/api/artemy/client';
import type { GroupId } from '@vlabs/api/artemy/model';
import { LockOffIcon, VideoOnIcon } from '@vlabs/icons';
import { Tag } from '@vlabs/uikit/components/tag';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { routes } from '@vlapps/point/src/app/routes';
import { generatePath } from 'react-router-dom';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './DepartmentStreamGroup.module.sass';
import { Link } from '../../../shared/ui/link';

interface DepartmentStreamGroupProps extends WithDataTestid {
  groupId: GroupId;
  actions?: React.ReactNode[];
}

const displayName = 'DepartmentStreamGroup';
const testid = buildTestid(displayName);

function DepartmentStreamGroup({
  groupId,
  actions = undefined,
  'data-testid': dataTestId = undefined,
}: DepartmentStreamGroupProps): React.JSX.Element {
  const { data: dataFromPoint } = useGetGroup(groupId);
  const permissionTag = dataFromPoint?.permission_tag;

  return (
    <div
      className={st.Root}
      {...testid(dataTestId)}
    >
      <div className={st.Info}>
        <VideoOnIcon className={st.Icon} />
        <Link
          to={generatePath(routes.streamGroups.view, { groupId })}
          type="secondary"
          {...testid('Link', dataTestId)}
        >
          {dataFromPoint?.group_name ?? dataFromPoint?.group_id}
        </Link>

        {!!permissionTag && (
          <Tag
            className={st.Tag}
            icon={<LockOffIcon />}
            kind="primary"
            shape="square"
            value={permissionTag}
            {...testid('Tag', dataTestId)}
          />
        )}
      </div>

      {!!actions && actions.length > 0 && (
        <div
          className={st.Actions}
          {...testid('Actions', dataTestId)}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

DepartmentStreamGroup.displayName = displayName;

export { DepartmentStreamGroup };
